import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ArrowRight,
  Building2,
  Check,
  Eye,
  FileText,
  Paperclip,
  PenLine,
  Send,
  Trash2,
  X,
} from "lucide-react";
import clsx from "clsx";
import {
  Contracts,
  MAX_CONTRACT_BYTES,
  type ContractDetail,
  type ContractEvent,
  type ContractSide,
} from "@/api/contracts";
import { apiErrorMessage } from "@/api/client";
import { fileSize } from "@/api/demos";
import { confirmDialog, toast } from "@/lib/feedback";
import { STATUS, statusColor } from "@/lib/contracts";
import { useAuthStore } from "@/store/auth";
import { Avatar, Segmented } from "@/components/ui";
import { DayDivider } from "@/components/deal/Chat";

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const firstName = (n: string | null) => n?.split(" ")[0] ?? null;

/** The contract's story: notes from both sides, and every status change, new
 *  version, edited term and finished obligation, as a messenger thread. */
export default function ContractThread({
  contract,
  onView,
}: {
  contract: ContractDetail;
  onView: (versionId: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const days = useMemo(() => {
    const out: { day: string; events: ContractEvent[] }[] = [];
    for (const e of contract.events) {
      const day = format(new Date(e.created_at), "yyyy-MM-dd");
      if (out.at(-1)?.day !== day) out.push({ day, events: [] });
      out.at(-1)!.events.push(e);
    }
    return out;
  }, [contract.events]);

  // Newest at the bottom, like a chat: stay scrolled there as entries arrive.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [contract.events.length, contract.last_event_at]);

  const them = contract.account_name ?? "Their side";
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[760px] space-y-4 px-4 py-5 md:px-6">
          {days.map(({ day, events }) => (
            <div key={day} className="space-y-4">
              <DayDivider day={day} />
              {events.map((e) => (
                <Entry key={e.id} event={e} contract={contract} them={them} onView={onView} />
              ))}
            </div>
          ))}
          {contract.events.length <= 1 && (
            <p className="py-4 text-center text-sm text-mut-500">
              Log how it's going: what their legal team asked for, what we agreed internally, and
              each new draft. Status changes and new versions appear here on their own.
            </p>
          )}
        </div>
      </div>
      <Composer contract={contract} them={them} />
    </div>
  );
}

function Entry({
  event: e,
  contract,
  them,
  onView,
}: {
  event: ContractEvent;
  contract: ContractDetail;
  them: string;
  onView: (versionId: string) => void;
}) {
  if (e.kind === "comment") return <Note event={e} contract={contract} them={them} />;
  if (e.kind === "status" && e.meta?.from && e.meta?.to) {
    return (
      <Centered>
        <span style={{ color: statusColor(e.meta.from) }}>{STATUS[e.meta.from].label}</span>
        <ArrowRight className="h-3.5 w-3.5 text-mut-500" />
        <span className="font-semibold" style={{ color: statusColor(e.meta.to) }}>
          {STATUS[e.meta.to].label}
        </span>
        <Byline event={e} />
      </Centered>
    );
  }
  if (e.kind === "version") {
    const v = contract.versions.find((x) => x.id === e.meta?.version_id);
    return (
      <div className="mx-auto w-full max-w-[460px] rounded-2xl border border-accent-azure/25 bg-accent-azure/[0.06] px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-accent-azure/15 text-accent-azure">
            <FileText className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-md font-semibold">Version {e.meta?.version}</div>
            <div className="truncate text-sm text-mut-500">
              {e.meta?.filename}
              {v ? ` · ${fileSize(v.size_bytes)}` : ""}
            </div>
          </div>
          {v && (
            <button className="btn !py-1" onClick={() => onView(v.id)}>
              <Eye className="h-3.5 w-3.5" /> View
            </button>
          )}
        </div>
        {e.body && <p className="mt-2 whitespace-pre-wrap text-md">{e.body}</p>}
        <div className="mt-1.5 text-xs text-mut-500">
          {e.author_name ?? "Someone"} · {time(e.created_at)}
        </div>
      </div>
    );
  }
  if (e.kind === "terms") {
    return (
      <div className="mx-auto w-full max-w-[460px] rounded-2xl border border-fg/[0.08] bg-ink-900 px-4 py-2.5 text-sm">
        <div className="mb-1 flex items-center gap-1.5 font-medium">
          <PenLine className="h-3.5 w-3.5 text-accent-violet" /> Terms updated
          <Byline event={e} />
        </div>
        <ul className="space-y-0.5 text-mut-300">
          {(e.meta?.changes ?? []).map((ch) => (
            <li key={ch.field}>
              <span className="text-mut-500">{ch.field}:</span>{" "}
              {ch.to === "updated" ? (
                "updated"
              ) : (
                <>
                  <span className="line-through decoration-mut-500/60">{ch.from}</span> →{" "}
                  <span className="font-medium text-mut-100">{ch.to}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (e.kind === "milestone") {
    return (
      <Centered>
        <Check className="h-3.5 w-3.5 text-accent-green" />
        <span>
          Done: <span className="font-medium">{e.body}</span>
        </span>
        <Byline event={e} />
      </Centered>
    );
  }
  return (
    <Centered>
      <span>Contract opened</span>
      <Byline event={e} />
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex justify-center">
      <span className="inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 rounded-full border border-fg/[0.08] bg-ink-900 px-3 py-1 text-sm">
        {children}
      </span>
    </div>
  );
}

function Byline({ event: e }: { event: ContractEvent }) {
  return (
    <span className="text-xs text-mut-500">
      {e.author_name ? `· ${firstName(e.author_name)} ` : ""}· {time(e.created_at)}
    </span>
  );
}

/** A note as a chat bubble: ours on the right, theirs (logged by us) on the left. */
function Note({
  event: e,
  contract,
  them,
}: {
  event: ContractEvent;
  contract: ContractDetail;
  them: string;
}) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const theirs = e.side === "them";
  const canDelete = e.author_user_id === me?.id || me?.role === "admin";
  const remove = async () => {
    if (
      !(await confirmDialog({ title: "Delete this note?", confirmLabel: "Delete", danger: true }))
    )
      return;
    try {
      await Contracts.removeComment(contract.id, e.id);
      await qc.invalidateQueries({ queryKey: ["contract", contract.id] });
    } catch (err) {
      toast.err(apiErrorMessage(err));
    }
  };
  return (
    <div className={clsx("group flex items-end gap-2.5", !theirs && "flex-row-reverse")}>
      {theirs ? (
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-fg/[0.06] text-mut-300">
          <Building2 className="h-3.5 w-3.5" />
        </span>
      ) : (
        <Avatar name={e.author_name} size={28} />
      )}
      <div
        className={clsx("flex min-w-0 max-w-[80%] flex-col", theirs ? "items-start" : "items-end")}
      >
        <div
          className={clsx(
            "mb-1 flex items-center gap-1.5 text-xs text-mut-500",
            !theirs && "flex-row-reverse",
          )}
        >
          <span className="font-medium text-mut-300">
            {theirs ? them : (e.author_name ?? "Us")}
          </span>
          {theirs && e.author_name && <span>logged by {firstName(e.author_name)}</span>}
          <span className="tabular-nums">{time(e.created_at)}</span>
          {canDelete && (
            <button
              className="opacity-0 transition-opacity hover:text-accent-pink focus:opacity-100 group-hover:opacity-100"
              aria-label="Delete note"
              onClick={() => void remove()}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
        <div
          className={clsx(
            "whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-md leading-6",
            theirs
              ? "rounded-bl-md border border-fg/[0.08] bg-ink-900"
              : "rounded-br-md border border-accent-violet/25 bg-accent-violet/[0.09]",
          )}
        >
          {e.body}
        </div>
      </div>
    </div>
  );
}

function Composer({ contract, them }: { contract: ContractDetail; them: string }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [side, setSide] = useState<ContractSide>("us");
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);

  const send = useMutation({
    mutationFn: () =>
      file
        ? Contracts.uploadVersion(contract.id, file, text.trim(), setProgress)
        : Contracts.comment(contract.id, text.trim(), side),
    onSuccess: (d) => {
      qc.setQueryData(["contract", contract.id], d);
      void qc.invalidateQueries({ queryKey: ["contracts"] });
      if (file) toast.ok(`Version ${d.versions[0]?.version} uploaded`);
      setText("");
      setFile(null);
      setProgress(null);
    },
    onError: (e) => {
      setProgress(null);
      toast.err(apiErrorMessage(e));
    },
  });
  const ready = (file || text.trim()) && !send.isPending;

  return (
    <div className="border-t border-fg/[0.07] bg-ink-950/90 px-3 py-3 backdrop-blur md:px-6">
      <div className="mx-auto max-w-[760px] space-y-2">
        <div className="flex items-center gap-2">
          {file ? (
            <span className="text-sm text-mut-500">New version · the text below is its note</span>
          ) : (
            <Segmented<ContractSide>
              label="Who said it"
              value={side}
              onChange={setSide}
              options={[
                { value: "us", label: "Our note" },
                { value: "them", label: `From ${them.length > 18 ? "them" : them}` },
              ]}
            />
          )}
        </div>
        {file && (
          <div className="flex items-center gap-2 rounded-xl border border-accent-azure/30 bg-accent-azure/[0.06] px-3 py-1.5 text-sm">
            <FileText className="h-3.5 w-3.5 text-accent-azure" />
            <span className="min-w-0 flex-1 truncate">{file.name}</span>
            <span className="text-mut-500">
              {progress != null ? `${Math.round(progress * 100)}%` : fileSize(file.size)}
            </span>
            <button
              aria-label="Remove file"
              onClick={() => setFile(null)}
              disabled={send.isPending}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <button
            className="btn btn-ghost !p-2"
            title="Upload a new version"
            aria-label="Upload a new version"
            onClick={() => fileRef.current?.click()}
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <textarea
            className="input min-h-[44px] flex-1 resize-none"
            rows={1}
            placeholder={
              file
                ? "What changed in this version? (optional)"
                : side === "them"
                  ? "What did their side say or ask for?"
                  : "Write an update…"
            }
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && ready) send.mutate();
            }}
          />
          <button
            className="btn btn-primary !px-3"
            disabled={!ready}
            aria-label="Send"
            onClick={() => send.mutate()}
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (!f) return;
            if (f.size > MAX_CONTRACT_BYTES) toast.err("Contract files can be up to 50 MB");
            else setFile(f);
          }}
        />
      </div>
    </div>
  );
}
